import type {BoardItemsMove} from '../types'
import type {BoardCardDragData, BoardCellDropData} from './types'

/**
 * What a drop makes of the card that was carried and the cell it landed on.
 *
 * No React, so it is exercised with no live `DndContext` around it, the way
 * `packages/workstreams-viewer/components/board/board-dnd.ts` keeps its own resolution
 * pure for the same reason.
 *
 * The payloads arrive already read, by `drag/types.ts`'s own readers, so everything about
 * a foreign or malformed payload is settled before this is called and both of those reach
 * here as `null`.
 */

/**
 * The move a drop describes, or `null` when it describes none.
 *
 * `null` for a drag this package could not read, for a drop on anything that is not one of
 * its cells, and for a drop that lands where the card already was.
 *
 * **A drop is a non-move only when both halves match.** A card can change column and keep
 * its row, change row and keep its column, or change both, and each of the three is a real
 * gesture that changes at least one field. Testing the column alone would silently discard
 * every vertical drag on a swimlaned board, and there would be nothing on screen or in a
 * log to say why the board had ignored somebody.
 *
 * Suppressed here rather than reported for a caller to guard against, because there is
 * nothing for such a drop to change: within one cell the only ordering is the active sort,
 * so a move that leaves both keys alone leaves the board exactly as it was.
 */
export function resolveBoardDrop(
  drag: BoardCardDragData | null | undefined,
  drop: BoardCellDropData | null | undefined,
): BoardItemsMove | null {
  if (!drag || !drop) return null
  if (drag.columnKey === drop.columnKey && drag.rowKey === drop.rowKey) return null

  return {
    itemIds: [drag.itemId],
    from: {columnKey: drag.columnKey, rowKey: drag.rowKey},
    to: {columnKey: drop.columnKey, rowKey: drop.rowKey},
  }
}
